// AssignClientModal - modal przypisania klienta (z traderId IS NULL) do zalogowanego handlowca.
//
// Renderowany przez WiseTableNoContextAction (method: OpenModalComponent, component_name: AssignClientModal)
// Po sukcesie wola onSuccess(), ktore zamyka modal i odswieza tabele klientow.

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetAssignableClients, usePostAssignTraderToClient } from 'api';

import styles from 'theme/components/containers/AssignClientModal/AssignClientModal.module.scss';

interface IProps {
  onCancel: () => void;
  onSuccess: () => void;
}

const AssignClientModal: React.FC<IProps> = ({ onCancel, onSuccess }) => {
  const { t } = useTranslation();

  const [clientId, setClientId] = useState<number | null>(null);
  const [clientName, setClientName] = useState<string>('');
  const [clientSearch, setClientSearch] = useState<string>('');
  const [clientDropdownOpen, setClientDropdownOpen] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const clientWrapperRef = useRef<HTMLDivElement | null>(null);

  const { data: clientsData, isFetching: isFetchingClients } = useGetAssignableClients(
    clientSearch,
    { enabled: clientDropdownOpen && clientSearch.trim().length >= 2 }
  );

  const assignMutation = usePostAssignTraderToClient({
    onSuccess: () => {
      onSuccess();
    },
    onError: (err) => {
      setSubmitError(err?.message ?? t('Nie udalo sie przypisac klienta'));
    }
  });

  useEffect(() => {
    if (!clientDropdownOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (clientWrapperRef.current && !clientWrapperRef.current.contains(e.target as Node)) {
        setClientDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [clientDropdownOpen]);

  const handleSelectClient = (id: number, label: string) => {
    setClientId(id);
    setClientName(label);
    setClientSearch(label);
    setClientDropdownOpen(false);
  };

  const handleClearClient = () => {
    setClientId(null);
    setClientName('');
    setClientSearch('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!clientId) {
      setSubmitError(t('Wybierz klienta z listy'));
      return;
    }

    assignMutation.mutate(clientId);
  };

  const isSubmitting = assignMutation.isLoading;

  return (
    <form className={styles.wrapper} onSubmit={handleSubmit}>
      <h2 className={styles.title}>{t('Przypisz klienta')}</h2>

      <div className={styles.field} ref={clientWrapperRef}>
        <label htmlFor="assign-client-search" className={styles.label}>
          {t('Klient')} *
        </label>
        <div className={styles.clientPicker}>
          <input
            id="assign-client-search"
            type="search"
            className={styles.input}
            value={clientSearch}
            onChange={(e) => {
              setClientSearch(e.target.value);
              setClientDropdownOpen(true);
              if (clientId && e.target.value !== clientName) {
                setClientId(null);
              }
            }}
            onFocus={() => setClientDropdownOpen(true)}
            placeholder={t('Szukaj klienta (min. 2 znaki)')}
            disabled={isSubmitting}
            autoComplete="off"
            autoFocus
          />
          {clientId && (
            <button
              type="button"
              className={styles.clearBtn}
              onClick={handleClearClient}
              disabled={isSubmitting}
              title={t('Wyczysc')}
            >
              x
            </button>
          )}
          {clientDropdownOpen && clientSearch.trim().length >= 2 && (
            <div className={styles.dropdown}>
              {isFetchingClients && (
                <div className={styles.dropdownInfo}>{t('Ladowanie...')}</div>
              )}
              {!isFetchingClients && (clientsData?.items ?? []).length === 0 && (
                <div className={styles.dropdownInfo}>{t('Brak wynikow')}</div>
              )}
              {!isFetchingClients && (clientsData?.items ?? []).length > 0 && (
                <ul className={styles.dropdownList}>
                  {(clientsData?.items ?? []).map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        className={styles.dropdownItem}
                        onClick={() => handleSelectClient(c.id, c.name ?? `#${c.id}`)}
                      >
                        <span className={styles.itemName}>{c.name ?? `#${c.id}`}</span>
                        {c.email && <span className={styles.itemMeta}>{c.email}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      {submitError && <div className={styles.error}>{submitError}</div>}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.cancelBtn}
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {t('Anuluj')}
        </button>
        <button
          type="submit"
          className={styles.submitBtn}
          disabled={!clientId || isSubmitting}
        >
          {isSubmitting ? t('Zapisywanie...') : t('Przypisz')}
        </button>
      </div>
    </form>
  );
};

export default AssignClientModal;
